import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * The reader's bookmark as a chapter ruler: read chapters in ink, unread ones blank.
 * Drag or click to move it; arrow keys step one chapter, PageUp/PageDown ten.
 * Only the position is shown here; what is visible is decided server-side.
 */
export function BookmarkRuler(props: {
  first: number;
  last: number;
  value: number;
  onChange: (n: number) => void;
  /** Title of the current chapter (known: it is at the bookmark). */
  current?: string;
  /** Short feedback after a move, e.g. "+3 namen". */
  gain?: string | null;
  extra?: ReactNode;
}) {
  const { first, last, value, onChange } = props;
  const count = last - first + 1;
  const ticks = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const shown = drag ?? value;
  const pos = (n: number) => `${((n - first + 1) / count) * 100}%`;

  const numRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = numRef.current;
    if (!el) return;
    el.classList.remove("tick");
    void el.offsetWidth;
    el.classList.add("tick");
  }, [value]);

  const chapterAt = (x: number) => {
    const r = ticks.current?.getBoundingClientRect();
    if (!r || r.width === 0) return value;
    return Math.min(last, Math.max(first, first + Math.ceil(((x - r.left) / r.width) * count) - 1));
  };
  const clamp = (n: number) => Math.min(last, Math.max(first, n));

  return (
    <div className="bookmark" role="group" aria-label="Boekenlegger">
      <div className="bookmark-head">
        <span className="bookmark-label">Gelezen t/m hoofdstuk</span>
        <span className="bookmark-num" ref={numRef}>
          {shown}
        </span>
        <span className="bookmark-of">/{last}</span>
        {props.current && <span className="bookmark-current">{props.current}</span>}
        <span className={`gain ${props.gain ? "show" : ""}`} aria-live="polite">
          {props.gain}
        </span>
        <span className="bookmark-step">
          {props.extra}
          <button className="btn btn-ghost" aria-label="Vorig hoofdstuk" disabled={value <= first}
                  onClick={() => onChange(value - 1)}>
            ‹
          </button>
          <button className="btn btn-ghost" aria-label="Volgend hoofdstuk" disabled={value >= last}
                  onClick={() => onChange(value + 1)}>
            ›
          </button>
        </span>
      </div>
      <div
        className={`ruler ${drag !== null ? "drag" : ""}`}
        role="slider"
        tabIndex={0}
        aria-label="Boekenlegger"
        aria-valuemin={first}
        aria-valuemax={last}
        aria-valuenow={shown}
        aria-valuetext={`hoofdstuk ${shown} van ${last}`}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          setDrag(chapterAt(e.clientX));
        }}
        onPointerMove={(e) => {
          const c = chapterAt(e.clientX);
          setHover(c);
          if (drag !== null) setDrag(c);
        }}
        onPointerLeave={() => setHover(null)}
        onPointerUp={() => {
          if (drag !== null && drag !== value) onChange(drag);
          setDrag(null);
        }}
        onPointerCancel={() => setDrag(null)}
        onKeyDown={(e) => {
          const step: Record<string, number> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 10, PageDown: -10 };
          let next: number | null = null;
          if (e.key in step) next = clamp(value + step[e.key]);
          else if (e.key === "Home") next = first;
          else if (e.key === "End") next = last;
          if (next === null) return;
          e.preventDefault();
          if (next !== value) onChange(next);
        }}
      >
        <div className="ruler-ticks" ref={ticks}>
          {Array.from({ length: count }, (_, i) => first + i).map((n) => (
            <i key={n} className={`${n > shown ? "unread" : ""} ${n === hover ? "hov" : ""}`} />
          ))}
        </div>
        <div className="ruler-handle" style={{ left: pos(shown) }} />
        {hover !== null && (
          <span className="ruler-hover" style={{ left: `calc(${pos(hover)} - ${50 / count}%)` }}>
            H{hover}
          </span>
        )}
      </div>
    </div>
  );
}

/** Briefly show a message, then clear it (micro-feedback after an action). */
export function useFlash(ms = 1800): [string | null, (msg: string | null) => void] {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return [
    msg,
    (m) => {
      setMsg(m);
      window.clearTimeout(timer.current);
      if (m) timer.current = window.setTimeout(() => setMsg(null), ms);
    },
  ];
}

/** One cell per chapter: a book at a glance (library, progress). */
export function ChapterStrip({ count, cls }: { count: number; cls: (i: number, n: number) => string }) {
  const n = Math.max(1, Math.min(count, 200));
  return (
    <div className="strip" aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <i key={i} className={cls(i, n)} />
      ))}
    </div>
  );
}
